import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, Phone, Mail, Facebook, Instagram } from "lucide-react";

const Contact = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0]">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-start">
            <div>
              <Button
                variant="ghost"
                className="text-white hover:text-white/80 mb-4"
                onClick={() => navigate("/")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Home
              </Button>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2" data-testid="contact-title">
                Contact
              </h1>
              <p className="text-xl text-white/90">Get in touch with LeProfBerni</p>
            </div>
            <img 
              src="https://customer-assets.emergentagent.com/job_languagecoach/artifacts/iatwncw6_Logo%20le%20prof.png" 
              alt="Le Prof Berni Logo" 
              className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-lg"
              data-testid="contact-logo"
            />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Contact Information Card */}
        <Card className="shadow-2xl mb-8" data-testid="contact-card">
          <CardContent className="p-8 sm:p-12">
            <h2 className="text-3xl font-bold text-[#04539F] mb-8 text-center">Let's Connect</h2>
            
            <div className="grid lg:grid-cols-[300px_1fr] md:grid-cols-1 gap-8 items-start">
              {/* Photo */}
              <div className="flex justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_languagecoach/artifacts/obwh33u0_bern%20for%20web.jpg"
                  alt="Bernard Alcindor - Le Prof Berni"
                  className="w-64 h-64 rounded-full object-cover border-4 border-[#04539F] shadow-xl"
                  data-testid="contact-photo"
                />
              </div>

              {/* Contact Info */}
              <div className="space-y-6 min-w-0">
              {/* Phone */}
              <a 
                href="tel:+32492956482"
                className="flex items-center gap-4 p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group"
                data-testid="contact-phone"
              >
                <div className="w-12 h-12 bg-[#04539F] rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-semibold">Phone</p>
                  <p className="text-lg text-gray-900 font-medium">+32 492 95 64 82</p>
                </div>
              </a>

              {/* Email */}
              <a 
                href="mailto:bernard.alcindor@leprofberni.com"
                className="flex items-center gap-4 p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group"
                data-testid="contact-email"
              >
                <div className="w-12 h-12 bg-[#04539F] rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-gray-500 font-semibold">Email</p>
                  <p className="text-base sm:text-lg text-gray-900 font-medium break-words overflow-wrap-anywhere">bernard.alcindor@leprofberni.com</p>
                </div>
              </a>
              </div>
            </div>

            {/* Social Media */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h3 className="text-2xl font-bold text-[#04539F] mb-6 text-center">Follow Me</h3>
              <div className="flex justify-center gap-6">
                {/* Facebook */}
                <a
                  href="https://www.facebook.com/profile.php?id=61569447875760"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-16 h-16 bg-[#04539F] rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-lg hover:shadow-xl"
                  data-testid="contact-facebook"
                >
                  <Facebook className="w-8 h-8 text-white fill-white" />
                </a>

                {/* Instagram */}
                <a
                  href="https://www.instagram.com/leprofberni/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-16 h-16 bg-[#04539F] rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-lg hover:shadow-xl"
                  data-testid="contact-instagram"
                >
                  <Instagram className="w-8 h-8 text-white" />
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center">
          <Button
            size="lg"
            className="bg-white text-[#04539F] hover:bg-white/90 font-semibold px-8 py-6 text-lg rounded-full transition-all hover:scale-105"
            onClick={() => navigate("/")}
            data-testid="back-home-btn"
          >
            Back to Home
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#04539F] text-white py-8 px-4 sm:px-6 lg:px-8 mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <a
            href="https://app.emergent.sh/?utm_source=emergent-badge"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white/80 text-xs transition-colors"
          >
            <img
              src="https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4"
              alt="Emergent"
              className="w-4 h-4"
            />
            Made with Emergent
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
